import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { getSubject } from '../authz/subject.js';
import { ClassRoom } from '../classroom.js';
import { Exam } from '../exam.js';
import { Submission } from '../submission.js';
import { createTeacherSummary } from './generate-grading-reports.js';

/**
 * Lightweight per-exam summary for a class's exam overview (see ClassRoster in classroom-page.tsx).
 * Computed server-side from the full exam/submission trees via {@link createTeacherSummary} — the
 * same aggregation the grading-report export uses — so the client only ever receives the small
 * rolled-up numbers, never the heavy task/submission trees themselves.
 */
@Entity()
export class ClassExamSummaryDTO extends AdornisEntity {
  static override _class = 'ClassExamSummaryDTO';

  /** Identity for the client cache, which refuses to MERGE an array of entities whose rows it cannot tell
   * apart — and these are returned as an array. `AdornisEntity` has no `_id` of its own, and the `_id`
   * passed to the constructor is not a declared field, so it cannot serve. One row per exam, one
   * conversation per exam: `conversationId` is the identity, and is already the React key on this list. */
  static override resolveID(data: any) {
    return data?.conversationId;
  }

  @Field(() => String)
  conversationId!: string;

  @Field(() => String)
  title: Maybe<string>;

  @Field(() => String)
  subject: Maybe<string>;

  @Field(() => String)
  status!: string;

  @Field(() => String)
  createdAt!: string;

  @Field(() => Int)
  submissionCount!: number;

  @Field(() => Float)
  averagePercentage!: number;

  @Field(() => Float)
  averageGrade: Maybe<number>;
}

export const getClassExamOverviewQuery = registerQuery({
  operationName: 'getClassExamOverviewQuery',
  type: () => [ClassExamSummaryDTO],
  params: [{ name: 'classRoomID', type: () => String }],

  resolve:
    (classRoomID: string) =>
    async (_selection: BaseQLSelectionSet<ClassExamSummaryDTO>): Promise<ClassExamSummaryDTO[]> => {
      const trimmedClassRoomID = typeof classRoomID === 'string' ? classRoomID.trim() : '';
      if (!trimmedClassRoomID) return [];

      const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
      if (!currentUser?._id) return [];

      const classroomCollection = await getCollection(ClassRoom);
      const classroom = await classroomCollection.findOne({ _id: trimmedClassRoomID });
      // The calling teacher's own classroom only (members may read a ClassRoom, but this overview is not theirs).
      const { teacherId } = await getSubject();
      if (!classroom || !teacherId || classroom.teacherID !== teacherId) return [];

      const examCollection = await getCollection(Exam);
      const submissionCollection = await getCollection(Submission);

      // Matches by the real `classRoomID` foreign key (see `linkExamToClassroom`, db/utils/classroom.ts)
      // OR, for exams created before that field existed, by the legacy (name, subject) string pair —
      // a class is scoped by subject (see ClassRoom.subject), so "9a" in math and "9a" in physics are
      // different classrooms and only exams tagged with both must show up here. `userID` scopes this to
      // the current teacher's own exams regardless of which branch matches.
      const exams = await examCollection
        .find({
          userID: currentUser._id,
          $or: [{ classRoomID: trimmedClassRoomID }, { class: classroom.name, subject: classroom.subject }],
        } as any)
        .sort({ createdAt: -1 })
        .toArray();

      const results: ClassExamSummaryDTO[] = [];
      for (const exam of exams) {
        const submissions = await submissionCollection.find({ examId: exam._id }).toArray();

        let averagePercentage = 0;
        let averageGrade: number | null = null;
        if (submissions.length > 0) {
          const summary = createTeacherSummary(exam, submissions as any[]);
          averagePercentage = summary.averagePercentage;
          averageGrade = summary.averageGrade;
        }

        results.push(
          new ClassExamSummaryDTO({
            _id: exam._id,
            conversationId: exam.conversationId,
            title: exam.title,
            subject: exam.subject,
            status: exam.status,
            createdAt: exam.createdAt,
            submissionCount: submissions.length,
            averagePercentage,
            averageGrade,
          }),
        );
      }

      return results;
    },
});
